import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Select } from "stories/Admin";
import useFetch from "hooks/useFetch";
import PageModal from "../LocationsPage/PageModal";
import SearchField, { matchesSearch } from "./SearchField";
import styles from "./ExternalPage.module.scss";
import {
  BonnetidCatalogSource,
  CatalogCandidate,
  ExternalJamatRegistration,
  SourceChangeResult,
  isValidSourceId,
} from "./bonnetidTypes";

interface SourceChangeDialogProps {
  registration: ExternalJamatRegistration | null;
  /** Forhåndsvalgt ny kilde, f.eks. et forslag fra «Sjekk manglende moskeer». */
  suggestedSource?: string;
  onClose: () => void;
  onChanged: (result: SourceChangeResult) => void;
}

const MAX_ROWS = 50;

/**
 * «Koble til annen kilde»: iBønn-moskeen (org.nr., følgere, push, admins, TV) beholdes, bare jamat-/jummah-tidene
 * hentes fra en annen Bonnetid-ID. Søker i samme katalog som import-lista.
 */
const SourceChangeDialog: React.FC<SourceChangeDialogProps> = ({
  registration,
  suggestedSource,
  onClose,
  onChanged,
}) => {
  const { execute, lastError } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const [catalogSource, setCatalogSource] = useState<BonnetidCatalogSource>("NEW");
  const [candidates, setCandidates] = useState<CatalogCandidate[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<SourceChangeResult | null>(null);

  const open = registration !== null;
  const currentSource = registration?.sourceOrgNr || registration?.orgNr || "";

  useEffect(() => {
    if (!open) return;
    setSelected(suggestedSource ?? "");
    setSearch(suggestedSource ?? "");
    setError("");
    setResult(null);
  }, [open, registration?.id, suggestedSource]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingCatalog(true);
    setCatalogError("");
    executeRef.current("/api/v1/admin/calendar/bonnetid/mosques", "GET", undefined, {
      source: catalogSource,
    }).then((rows) => {
      if (!active) return;
      if (rows === null) {
        setCatalogError(lastError()?.message || "Kunne ikke hente Bonnetid-katalogen.");
        setCandidates([]);
      } else {
        setCandidates(rows as CatalogCandidate[]);
      }
      setLoadingCatalog(false);
    });
    return () => {
      active = false;
    };
  }, [open, catalogSource]);

  const rows = useMemo(
    () =>
      candidates
        .filter((c) => c.orgNr !== currentSource)
        .filter((c) => matchesSearch(search, [c.name, c.orgNr, c.city, c.address, c.postalCode]))
        .slice(0, MAX_ROWS),
    [candidates, search, currentSource],
  );

  const typedId = search.trim();
  const canUseTyped =
    isValidSourceId(typedId) && typedId !== currentSource && !candidates.some((c) => c.orgNr === typedId);
  const selectedCandidate = candidates.find((c) => c.orgNr === selected);

  const submit = async () => {
    if (!registration || !selected) return;
    setBusy(true);
    setError("");
    const response = (await execute(
      `/api/v1/admin/external-jamat/registrations/${registration.id}/source`,
      "PATCH",
      { externalId: selected },
    )) as SourceChangeResult | null;
    setBusy(false);
    if (!response) {
      setError(lastError()?.message || "Kunne ikke bytte kilde.");
      return;
    }
    setResult(response);
    onChanged(response);
  };

  const name = registration?.mosqueName || registration?.orgNr;

  return (
    <PageModal
      open={open}
      title="Koble til annen kilde"
      icon="link"
      width={680}
      onClose={() => !busy && onClose()}
      footer={
        result ? (
          <Button onClick={onClose}>Lukk</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Avbryt
            </Button>
            <Button onClick={submit} loading={busy} disabled={!selected || busy}>
              Bytt kilde og synk
            </Button>
          </>
        )
      }
    >
      {registration && (
        <div className={styles.dialogBody}>
          <p>
            <b>{name}</b> · org.nr. i iBønn <b>{registration.orgNr}</b>
            <br />
            Gjeldende Bonnetid-kilde: <Badge tone="blue">{currentSource}</Badge>
            {registration.sourceChanged && <span className={styles.muted}> (byttet fra moskeens org.nr.)</span>}
          </p>

          {result ? (
            <>
              <Alert tone={result.syncLog.success ? "ok" : "warn"}>
                Kilden er byttet fra <b>{result.previousSourceOrgNr}</b> til{" "}
                <b>{result.registration.sourceOrgNr}</b>. Synk:{" "}
                {result.syncLog.success ? "OK" : "med feil"} – +{result.syncLog.jamatDaysCreated} jamat-dager, +
                {result.syncLog.jummahRowsCreated} jummah-rader
                {result.syncLog.jummahRowsReplaced > 0 && ` (${result.syncLog.jummahRowsReplaced} erstattet)`}.
              </Alert>
              {result.syncLog.errors.length > 0 && (
                <ul className={styles.errorList}>
                  {result.syncLog.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <div className={styles.dialogRow}>
                <Select
                  label="Katalog"
                  value={catalogSource}
                  disabled={busy}
                  onChange={(e) => setCatalogSource(e.target.value as BonnetidCatalogSource)}
                >
                  <option value="NEW">Nytt Bønnetid-API</option>
                  <option value="LEGACY">Gammelt Bønnetid-API</option>
                </Select>
              </div>
              <SearchField value={search} onChange={setSearch} placeholder="Søk etter navn, org.nr. eller by …" />
              {catalogError && <Alert tone="error">{catalogError}</Alert>}
              {loadingCatalog ? (
                <p className={styles.muted}>Henter Bonnetid-katalogen …</p>
              ) : (
                <ul className={styles.pickList} role="listbox" aria-label="Bonnetid-moskeer">
                  {canUseTyped && (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected === typedId}
                        className={selected === typedId ? styles.pickSelected : undefined}
                        onClick={() => setSelected(typedId)}
                      >
                        <b>Bruk ID-en «{typedId}»</b>
                        <span className={styles.muted}> – kontrolleres mot Bonnetid når du bytter</span>
                      </button>
                    </li>
                  )}
                  {rows.map((c) => (
                    <li key={c.orgNr}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected === c.orgNr}
                        className={selected === c.orgNr ? styles.pickSelected : undefined}
                        onClick={() => setSelected(c.orgNr)}
                      >
                        <b>{c.name}</b> · {c.orgNr}
                        <span className={styles.muted}>
                          {" "}
                          · {[c.address, c.postalCode, c.city].filter(Boolean).join(" ")}
                        </span>
                        {c.existing && <Badge tone="grey">Finnes i iBønn</Badge>}
                      </button>
                    </li>
                  ))}
                  {rows.length === 0 && !canUseTyped && <li className={styles.muted}>Ingen treff.</li>}
                </ul>
              )}
              {selected && (
                <Alert tone="info">
                  Ny kilde: <b>{selectedCandidate ? `${selectedCandidate.name} · ${selected}` : selected}</b>.{" "}
                  Moskeen beholder brukere, push og admins i iBønn – bare tidene hentes fra den nye kilden.
                  {selectedCandidate?.existing &&
                    " Kilden er også registrert som egen moské i iBønn; begge henter da tider fra samme kilde."}
                </Alert>
              )}
              {error && <Alert tone="error">{error}</Alert>}
            </>
          )}
        </div>
      )}
    </PageModal>
  );
};

export default SourceChangeDialog;
